import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { orderBy, limit } from "firebase/firestore";
import { useFirestoreCollection } from "../lib/useFirestoreCollection";
import styles from "./Portfolio.module.css";

const SKELETON_COUNT = 3;

// Extrae un resumen corto y limpio para la tarjeta (1-2 líneas concisas)
function getShortSummary(text) {
  if (!text) return "";
  const rawParagraphs = text
    .split(/\n+/)
    .map((p) => p.trim())
    .filter(Boolean);
  if (rawParagraphs.length === 0) return "";

  let target = rawParagraphs[0];
  // Si el primer párrafo es solo un titular corto y hay un párrafo descriptivo
  if (target.length < 80 && rawParagraphs[1]) {
    target = rawParagraphs[1];
  } else if (target.includes(":\n") || target.endsWith(":")) {
    target = rawParagraphs[1] || target;
  }

  // Eliminar prefijo de título duplicado si existe (ej. "Nombre: Descripción")
  const colonIdx = target.indexOf(": ");
  if (colonIdx > 0 && colonIdx < 45) {
    target = target.slice(colonIdx + 2).trim();
  }

  if (target.length > 155) {
    const cut = target.slice(0, 145);
    const lastSpace = cut.lastIndexOf(" ");
    return (lastSpace > 0 ? cut.slice(0, lastSpace) : cut) + "...";
  }
  return target;
}

// Divide el texto extenso en párrafos bien estructurados para la pestaña
function formatParagraphs(text) {
  if (!text) return [];
  return text
    .split(/\n+/)
    .map((p) => p.trim())
    .filter(Boolean);
}

export default function Portfolio() {
  const { items, loading } = useFirestoreCollection(
    "portfolio",
    orderBy("year", "desc"),
    limit(6)
  );
  const [activeItem, setActiveItem] = useState(null);
  const [viewMode, setViewMode] = useState("render"); // "render" | "real" | "split"
  const [detailTab, setDetailTab] = useState("descripcion"); // "descripcion" | "especificaciones"
  const [cardViews, setCardViews] = useState({});
  const [sliderPos, setSliderPos] = useState(50);
  const [isDraggingSlider, setIsDraggingSlider] = useState(false);

  useEffect(() => {
    if (!activeItem) return;
    const onKey = (e) => {
      if (e.key === "Escape") setActiveItem(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [activeItem]);

  function openModal(item) {
    setActiveItem(item);
    // Mostrar siempre una sola imagen a tamaño completo por defecto
    setViewMode(item.cover?.url ? "render" : "real");
    setDetailTab("descripcion");
    setSliderPos(50);
  }

  const handleSliderMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const clientX = e.clientX ?? (e.touches && e.touches[0]?.clientX);
    if (clientX == null || rect.width === 0) return;
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    setSliderPos(Math.round((x / rect.width) * 100));
  };

  return (
    <section id="portafolio" className={styles.wrap}>
      <header className={styles.head}>
        <span className="mono-tag">// 02 — Casos</span>
        <h2 className={styles.h2}>
          Lo que <span className={styles.alt}>hemos construido</span>.
        </h2>
        <p className={styles.lead}>
          Una selección de proyectos: equipos a medida, retrofits e instalaciones
          de instrumentación. Haz clic en cualquier caso para ampliar.
        </p>
      </header>

      {loading ? (
        <ul className={styles.grid} aria-hidden="true">
          {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
            <li key={i} className={`${styles.card} ${styles.skeleton}`}>
              <div className={`${styles.coverPh} ${styles.ghost}`} />
              <div className={styles.cardMeta}>
                <span className={`${styles.ghost} ${styles.ghostTag}`} />
                <span className={`${styles.ghost} ${styles.ghostTitle}`} />
                <span className={`${styles.ghost} ${styles.ghostLine}`} />
                <span className={`${styles.ghost} ${styles.ghostLineShort}`} />
              </div>
            </li>
          ))}
        </ul>
      ) : items.length > 0 ? (
        <ul className={styles.grid}>
          {items.map((c) => {
            const currentCardView =
              cardViews[c.id] || (c.cover?.url ? "render" : "real");

            return (
              <li
                key={c.id}
                className={`${styles.card} ${styles.cardInteractive}`}
                onClick={() => openModal(c)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    openModal(c);
                  }
                }}
                aria-label={`Ver detalles del proyecto ${c.title}`}
              >
                {/* Contenedor de imagen única a ancho completo (sin partir el cuadro en dos) */}
                <div
                  className={styles.imagesWrap}
                  data-view={currentCardView}
                >
                  {/* Selector de modo directamente en la tarjeta si tiene ambas imágenes */}
                  {c.cover?.url && c.realPhoto?.url && (
                    <div
                      className={styles.cardSwitchPill}
                      onClick={(e) => e.stopPropagation()}
                      role="tablist"
                      aria-label="Alternar vista en tarjeta"
                    >
                      <button
                        type="button"
                        role="tab"
                        aria-selected={currentCardView === "render"}
                        className={styles.cardSwitchBtn}
                        data-active={currentCardView === "render"}
                        onClick={() =>
                          setCardViews((prev) => ({
                            ...prev,
                            [c.id]: "render",
                          }))
                        }
                      >
                        Render
                      </button>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={currentCardView === "real"}
                        className={styles.cardSwitchBtn}
                        data-active={currentCardView === "real"}
                        onClick={() =>
                          setCardViews((prev) => ({
                            ...prev,
                            [c.id]: "real",
                          }))
                        }
                      >
                        Real
                      </button>
                    </div>
                  )}

                  {/* Imagen visible: ocupa el 100% del cuadro con fondo integrado */}
                  {currentCardView === "render" && c.cover?.url ? (
                    <div className={`${styles.shot} ${styles.shotRender}`}>
                      <img
                        src={c.cover.url}
                        alt={`Render CAD de ${c.title}`}
                      />
                      <span className={styles.shotTag}>Render CAD</span>
                    </div>
                  ) : c.realPhoto?.url ? (
                    <div className={`${styles.shot} ${styles.shotReal}`}>
                      <img
                        src={c.realPhoto.url}
                        alt={`Equipo fabricado: ${c.title}`}
                      />
                      <span className={styles.shotTag}>Equipo Real</span>
                    </div>
                  ) : c.cover?.url ? (
                    <div className={`${styles.shot} ${styles.shotRender}`}>
                      <img
                        src={c.cover.url}
                        alt={`Render CAD de ${c.title}`}
                      />
                      <span className={styles.shotTag}>Render CAD</span>
                    </div>
                  ) : (
                    <div className={styles.coverPh} />
                  )}

                  <span className={styles.zoomHint} aria-hidden="true">
                    Ver más ↗
                  </span>
                </div>

                <div className={styles.cardMeta}>
                  <div className={styles.cardTagRow}>
                    <span className="mono-tag">// {c.year}</span>
                    {c.realPhoto?.url && (
                      <span className={styles.cardBadge}>Fabricado</span>
                    )}
                  </div>
                  <h3 className={styles.cardTitle}>{c.title}</h3>
                  {c.summary && (
                    <p className={styles.summary}>
                      {getShortSummary(c.summary)}
                    </p>
                  )}
                  <div className={styles.cardActionRow}>
                    <span className={styles.seeMoreBtn}>
                      <span>Ver más</span>
                      <span className={styles.chev} aria-hidden="true">
                        →
                      </span>
                    </span>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      ) : null}

      {/* ── MODAL CON PESTAÑA DE DETALLE AL LADO DE LA IMAGEN ── */}
      {activeItem && (
        <div
          className={styles.modalBackdrop}
          onClick={() => setActiveItem(null)}
          role="dialog"
          aria-modal="true"
          aria-label={activeItem.title}
        >
          <div
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Cabecera superior del modal */}
            <header className={styles.modalHead}>
              <div className={styles.modalHeadInfo}>
                <span className="mono-tag">
                  // {activeItem.year} · INGENIERÍA AYA
                </span>
                <h3 className={styles.modalTitle}>{activeItem.title}</h3>
              </div>

              <button
                type="button"
                className={styles.modalClose}
                onClick={() => setActiveItem(null)}
                aria-label="Cerrar vista detallada"
              >
                ✕
              </button>
            </header>

            {/* Contenedor principal: Imagen en la izquierda + Pestaña de texto a su lado */}
            <div className={styles.modalBody}>
              {/* Lado Imagen */}
              <div className={styles.modalVisualStage}>
                {activeItem.cover?.url && activeItem.realPhoto?.url && (
                  <div className={styles.stageToolbar}>
                    <div className={styles.modeTabs} role="tablist">
                      <button
                        type="button"
                        className={styles.modeBtn}
                        data-active={viewMode === "split"}
                        onClick={() => setViewMode("split")}
                      >
                        Comparar
                      </button>
                      <button
                        type="button"
                        className={styles.modeBtn}
                        data-active={viewMode === "render"}
                        onClick={() => setViewMode("render")}
                      >
                        Render CAD
                      </button>
                      <button
                        type="button"
                        className={styles.modeBtn}
                        data-active={viewMode === "real"}
                        onClick={() => setViewMode("real")}
                      >
                        Foto Real
                      </button>
                    </div>
                  </div>
                )}

                <div
                  className={styles.modalStage}
                  data-view={viewMode}
                >
                  {viewMode === "split" &&
                  activeItem.cover?.url &&
                  activeItem.realPhoto?.url ? (
                    <div
                      className={styles.compareStage}
                      onPointerDown={(e) => {
                        setIsDraggingSlider(true);
                        handleSliderMove(e);
                      }}
                      onPointerMove={(e) => {
                        if (isDraggingSlider) handleSliderMove(e);
                      }}
                      onPointerUp={() => setIsDraggingSlider(false)}
                      onTouchMove={handleSliderMove}
                    >
                      {/* Capa base: Render CAD */}
                      <div className={styles.compareLayerBase}>
                        <img
                          src={activeItem.cover.url}
                          alt={`Render CAD de ${activeItem.title}`}
                        />
                        <span
                          className={`${styles.shotTag} ${styles.shotTagLeft}`}
                        >
                          Render CAD
                        </span>
                      </div>

                      {/* Capa superior: Foto Real revelada por el slider */}
                      <div
                        className={styles.compareLayerClip}
                        style={{ clipPath: `inset(0 0 0 ${sliderPos}%)` }}
                      >
                        <img
                          src={activeItem.realPhoto.url}
                          alt={`Equipo fabricado: ${activeItem.title}`}
                        />
                        <span
                          className={`${styles.shotTag} ${styles.shotTagRight}`}
                        >
                          Equipo Real
                        </span>
                      </div>

                      {/* Línea divisoria interactiva con perilla de arrastre */}
                      <div
                        className={styles.compareDivider}
                        style={{ left: `${sliderPos}%` }}
                      >
                        <div className={styles.compareHandle}>
                          <span>◀ ▶</span>
                        </div>
                      </div>
                    </div>
                  ) : viewMode === "real" && activeItem.realPhoto?.url ? (
                    <figure
                      className={`${styles.modalShot} ${styles.modalShotReal}`}
                    >
                      <img
                        src={activeItem.realPhoto.url}
                        alt={`Equipo fabricado: ${activeItem.title}`}
                      />
                      <figcaption className={styles.shotTag}>
                        Equipo Real
                      </figcaption>
                    </figure>
                  ) : (
                    <figure
                      className={`${styles.modalShot} ${styles.modalShotRender}`}
                    >
                      <img
                        src={
                          activeItem.cover?.url || activeItem.realPhoto?.url
                        }
                        alt={`Render CAD de ${activeItem.title}`}
                      />
                      <figcaption className={styles.shotTag}>
                        Render CAD
                      </figcaption>
                    </figure>
                  )}
                </div>
              </div>

              {/* Lado Pestaña / Panel de detalles al lado de la imagen */}
              <aside className={styles.modalTabPanel}>
                <div
                  className={styles.panelNavTabs}
                  role="tablist"
                  aria-label="Secciones de detalle"
                >
                  <button
                    type="button"
                    role="tab"
                    aria-selected={detailTab === "descripcion"}
                    className={styles.panelTabBtn}
                    data-active={detailTab === "descripcion"}
                    onClick={() => setDetailTab("descripcion")}
                  >
                    Descripción
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={detailTab === "especificaciones"}
                    className={styles.panelTabBtn}
                    data-active={detailTab === "especificaciones"}
                    onClick={() => setDetailTab("especificaciones")}
                  >
                    Especificaciones
                  </button>
                </div>

                <div className={styles.panelScrollArea}>
                  {detailTab === "descripcion" ? (
                    <div className={styles.panelTextContent}>
                      <span className={styles.panelBadge}>
                        MEMORIA DEL PROYECTO
                      </span>
                      {formatParagraphs(activeItem.summary).map(
                        (paragraph, idx) => (
                          <p key={idx} className={styles.panelParagraph}>
                            {paragraph}
                          </p>
                        )
                      )}
                    </div>
                  ) : (
                    <div className={styles.panelSpecsContent}>
                      <span className={styles.panelBadge}>
                        DATOS TÉCNICOS & DESARROLLO
                      </span>
                      <dl className={styles.specsList}>
                        <div className={styles.specItem}>
                          <dt className={styles.specLabel}>Año de desarrollo</dt>
                          <dd className={styles.specValue}>
                            {activeItem.year}
                          </dd>
                        </div>
                        <div className={styles.specItem}>
                          <dt className={styles.specLabel}>
                            Disciplina principal
                          </dt>
                          <dd className={styles.specValue}>
                            Diseño Mecánico · Mecatrónica · Fabricación a Medida
                          </dd>
                        </div>
                        <div className={styles.specItem}>
                          <dt className={styles.specLabel}>Estado del equipo</dt>
                          <dd className={styles.specValue}>
                            {activeItem.realPhoto?.url
                              ? "Fabricado, calibrado y en operación"
                              : "Diseño 3D e ingeniería validada"}
                          </dd>
                        </div>
                        <div className={styles.specItem}>
                          <dt className={styles.specLabel}>Documentación</dt>
                          <dd className={styles.specValue}>
                            Manual de operación, planos CAD y ficha de
                            mantenimiento
                          </dd>
                        </div>
                      </dl>
                    </div>
                  )}
                </div>

                <footer className={styles.panelFooter}>
                  <Link
                    to="/contacto"
                    className={styles.panelCta}
                    onClick={() => setActiveItem(null)}
                  >
                    <span>Cotizar proyecto similar</span>
                    <span aria-hidden="true">→</span>
                  </Link>
                </footer>
              </aside>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
